import { DOZER_CONFIG } from '../../config/dozer.config';
import type { HazardsConfig } from '../../config/hazards.config';
import { dozerStretch } from '../../dozer/dozerRules';
import type { HazardDefinition } from '../../hazards/HazardDefinition';
import { hazardOccupancy, hazardRequirements, type LaneStretch } from '../../hazards/hazardRules';
import type { ObstacleDefinition } from '../../obstacles/ObstacleDefinition';
import { ObstacleType } from '../../obstacles/ObstacleType';
import { doubleJumpStepReach, doubleJumpTriggerWindow, highestDoubleJump, jumpHeightAt, type StepReach, type TimeWindow } from '../../physics/JumpReach';
import type { SegmentDefinition, TrackArea } from '../segments/SegmentDefinition';
import type { LayoutRules } from './LayoutRules';
import { EMPTY_TERRAIN } from './terrainQueries';

/**
 * Obstacles with a walkable top that only a double jump gets onto (the container OBS_CONTAINER_A, roof
 * 2.69 m). User 2026-10-02: the lane before a container stays free long enough that the double jump onto
 * the roof can be timed cleanly at every speed. The length follows from the jump model – exactly as the
 * terrain analysis proves the container row of SEG_CONTAINERS_40M fair (terrainAnalysis.checkClimb):
 *
 *   take-off   meeting the face `arrival.from` … `arrival.to` seconds after take-off leaves the second jump
 *              at least minTriggerWindow of input tolerance (physics/JumpReach.ts, fixed steps like the game;
 *              2.69 m: 0.49–0.99 s). minTakeOffWindow (0.25 s) of take-off timing at top speed needs the
 *              runner's front up to (arrival.from + minTakeOffWindow) × maxSpeed before the face:
 *              0.743 s × 18 m/s = 13.37 m.
 *   run-up     the lane is plain road over that stretch plus half the body (runner centre → face):
 *              13.67 m – checkClimb's runUpNeeded. Slower runners get more timing out of it (8–14 m/s: the
 *              full 0.5 s of arrival times), so top speed is the binding case.
 *   before it  the runner has got past whatever stood in the lane before and is back on the road, free to
 *              take off (input buffered on landing) – along that obstacle's ideal line at top speed:
 *                jump          apex over its middle, lands jumpVelocity/g = 0.375 s after it: middle + 6.75 m
 *                double jump   second jump at the apex, the top (3.0 m) over its middle, lands 0.53 s after: + 9.5 m
 *                slide         body out from under it: back edge + half the body
 *                lane change   back into the lane once past it: + laneChangeDuration (0.24 s = 4.3 m)
 *                walkable top  drop off its end: + 0.50 s = 9.0 m (a second container in the lane)
 *              Pits count as jumps, raised ground as a drop, ceilings as slides; moving hazards by what they
 *              ask of the lane (beams: slide, the rest: lane change).
 *   drop zone  behind the roof (user 2026-10-02): a runner who runs off its end falls once its rear has left
 *              the top (front 0.6 m past the end), lands sqrt(2 · 2.69 m / g) = 0.50 s later and then needs one
 *              reaction time (0.3 s) before its front meets the next thing in the lane – the drop zone of the
 *              walkable decks (terrainAnalysis.findDecks): 0.6 m + (0.50 + 0.3) s × 18 m/s = 15.0 m after the
 *              rear edge stay free of obstacles, terrain and moving hazards.
 *
 * Example: a pallet (jump) in the container's lane leaves 6.75 + 13.67 = 20.4 m from its middle to the face.
 */
export interface RoofClimb {
  /** Height of the walkable top above the ground the obstacle stands on (m). */
  readonly rise: number;
  /** Timing tolerance of the double jump onto it (speed independent). */
  readonly reach: StepReach;
  /** Arrival times at the face (s after take-off) that leave the required second-jump tolerance. */
  readonly arrival: TimeWindow;
  /** Latest take-off the run-up must offer (s before the front meets the face): arrival.from + minTakeOffWindow. */
  readonly latestTakeOff: number;
  /** Run-up that take-off timing needs at top speed (front of the runner → face, m). */
  readonly takeOffRunUp: number;
  /** Plain road the lane keeps before the face, the runner back on it (runner centre → face, m). */
  readonly runUp: number;
  /** Free lane behind the rear edge (m): body depth + (fall off the top + reaction time) × top speed. */
  readonly dropZone: number;
}

/** Extent of an obstacle's colliders along the track (obstacle-local z: front and back). */
export interface ObstacleSpan {
  readonly front: number;
  readonly back: number;
}

const EPSILON = 1e-6;

export function obstacleSpan(definition: ObstacleDefinition): ObstacleSpan {
  let front = Infinity;
  let back = -Infinity;
  for (const c of definition.colliders) {
    front = Math.min(front, c.center.z - c.size.z / 2);
    back = Math.max(back, c.center.z + c.size.z / 2);
  }
  return { front, back };
}

/** Height of an obstacle's top above the ground it stands on (m). */
export function obstacleTop(definition: ObstacleDefinition): number {
  return Math.max(...definition.colliders.map((c) => c.center.y + c.size.y / 2));
}

/**
 * The climb onto an obstacle's walkable top, or null: no walkable top, a single jump gets up (no double-jump
 * run-up to keep), or the top lies beyond a fair double jump (no route at all).
 */
export function roofClimbOf(definition: ObstacleDefinition, rules: LayoutRules): RoofClimb | null {
  if (!definition.walkableTop) return null;
  const rise = obstacleTop(definition);
  if (rise <= rules.jumpHeight - rules.maxStepHeight + EPSILON) return null;
  const { minTriggerWindow, minTakeOffWindow } = rules.doubleJumpStep;
  const reach = doubleJumpStepReach(rules.physics, rules.maxStepHeight, rise, minTriggerWindow, rules.simulationStep);
  if (!reach.arrival) return null;
  const { maxSpeed, reactionTime } = rules.sequence;
  const latestTakeOff = reach.arrival.from + minTakeOffWindow;
  const takeOffRunUp = latestTakeOff * maxSpeed;
  return {
    rise,
    reach,
    arrival: reach.arrival,
    latestTakeOff,
    takeOffRunUp,
    runUp: takeOffRunUp + rules.playerDepth / 2,
    dropZone: rules.playerDepth + (fallTime(rise, rules) + reactionTime) * maxSpeed,
  };
}

/** Seconds a runner falls from `height` above the road. */
function fallTime(height: number, rules: LayoutRules): number {
  return height > EPSILON ? Math.sqrt((2 * height) / rules.physics.gravity) : 0;
}

/**
 * Where a runner who got past something in its lane along the ideal line is back on the road at top speed
 * (runner centre, same z frame): `middle` / `back` of the thing, `type` what it asks for, `top` a walkable top.
 */
export function backOnTheRoad(type: ObstacleType, middle: number, back: number, rules: LayoutRules, top = 0): number {
  const speed = rules.sequence.maxSpeed;
  const out = back + rules.playerDepth / 2;
  const drop = out + fallTime(top, rules) * speed;
  switch (type) {
    case ObstacleType.Jump:
      return Math.max(drop, middle + (rules.physics.jumpVelocity / rules.physics.gravity) * speed);
    case ObstacleType.DoubleJump:
      return Math.max(drop, middle + fallTime(highestDoubleJump(rules.physics, rules.simulationStep), rules) * speed);
    case ObstacleType.Slide:
      return drop;
    default:
      // Lane change (and anything else that is not jumped or slid under): back into the lane once past it.
      return Math.max(drop, out + rules.sequence.laneChangeDuration * speed);
  }
}

/** What an obstacle at `z` keeps busy in its lane (same z frame as `z`): from its front until a runner who got past it is back on the road. */
export function obstacleStretch(definition: ObstacleDefinition, lane: number, z: number, rules: LayoutRules): LaneStretch {
  const span = obstacleSpan(definition);
  const top = definition.walkableTop ? obstacleTop(definition) : 0;
  return { lane, zFrom: z + span.front, zTo: backOnTheRoad(definition.type, z + (span.front + span.back) / 2, z + span.back, rules, top) };
}

/**
 * The stretch of its lane an obstacle with a walkable top at `z` needs for itself: the run-up before its
 * face (runner centre) and its own footprint – nothing may stand under, over or in it.
 */
export function runUpStretch(definition: ObstacleDefinition, climb: RoofClimb, lane: number, z: number): LaneStretch {
  const span = obstacleSpan(definition);
  return { lane, zFrom: z + span.front - climb.runUp, zTo: z + span.back };
}

/** The drop zone behind an obstacle with a walkable top at `z`: no obstacle, terrain or moving hazard may be in it. */
export function dropZoneStretch(definition: ObstacleDefinition, climb: RoofClimb, lane: number, z: number): LaneStretch {
  const back = z + obstacleSpan(definition).back;
  return { lane, zFrom: back, zTo: back + climb.dropZone };
}

/** The ground an obstacle at `z` stands on (its colliders along the track). */
export function obstacleFootprint(definition: ObstacleDefinition, lane: number, z: number): LaneStretch {
  const span = obstacleSpan(definition);
  return { lane, zFrom: z + span.front, zTo: z + span.back };
}

/** Two stretches of the same lane overlap (touching ends do not). */
export function stretchesOverlap(a: LaneStretch, b: LaneStretch): boolean {
  return a.lane === b.lane && a.zTo > b.zFrom + EPSILON && a.zFrom < b.zTo - EPSILON;
}

/**
 * Where the terrain of a segment is no plain road, per lane (segment-local z, as busy stretches): raised or
 * sunken ground and the drop off its end, pits (jumped), every ceiling or slab over the running line (slid
 * under or walked on). A run-up needs plain road under open sky – the double jump lifts the head to 4.8 m.
 */
export function terrainStretches(definition: SegmentDefinition, rules: LayoutRules): LaneStretch[] {
  const { surfaces, gaps, ceilings } = definition.terrain ?? EMPTY_TERRAIN;
  const base = rules.groundHeight;
  const half = rules.playerWidth / 2;
  const stretches: LaneStretch[] = [];
  for (const { index: lane, centerX } of definition.lanes) {
    const covers = (area: TrackArea) => area.xTo > centerX - half && area.xFrom < centerX + half;
    const busy = (area: TrackArea, type: ObstacleType, top = 0) =>
      stretches.push({ lane, zFrom: area.zFrom, zTo: backOnTheRoad(type, (area.zFrom + area.zTo) / 2, area.zTo, rules, top) });
    for (const surface of surfaces) {
      if (covers(surface) && (Math.abs(surface.heightFrom - base) > EPSILON || Math.abs(surface.heightTo - base) > EPSILON)) {
        busy(surface, ObstacleType.Slide, surface.heightTo - base);
      }
    }
    for (const gap of gaps) if (covers(gap)) busy(gap, ObstacleType.Jump);
    for (const ceiling of ceilings) if (covers(ceiling)) busy(ceiling, ObstacleType.Slide, (ceiling.top ?? base) - base);
  }
  return stretches;
}

/** What the active moving hazards keep busy (segment-local): the ground they need, until a runner who got past is back on the road. */
export function hazardStretches(hazard: HazardDefinition, config: HazardsConfig, laneCount: number, rules: LayoutRules): LaneStretch[] {
  const asks = hazardRequirements(hazard, laneCount);
  return hazardOccupancy(hazard, config, laneCount).map((s) => {
    const type = asks.find((r) => r.lane === s.lane)?.type ?? ObstacleType.LaneChange;
    return { lane: s.lane, zFrom: s.zFrom, zTo: backOnTheRoad(type, (s.zFrom + s.zTo) / 2, s.zTo, rules) };
  });
}

/**
 * Everything of a module that is no plain road for a runner who drops into it from a container roof just
 * before it (segment-local): its terrain, its enclosure (tunnel, hall, shell: portal and roof over every lane –
 * the tunnel roof at 4.2 m is below the head of a runner on a container, 4.5 m), the ground any of its moving
 * hazards may need (whichever layout comes) and the parked dozer of a dozer module with its approach.
 */
export function moduleStretches(definition: SegmentDefinition, rules: LayoutRules, config: HazardsConfig): LaneStretch[] {
  const enclosure = definition.enclosure;
  return [
    ...terrainStretches(definition, rules),
    ...(enclosure ? definition.lanes.map((lane) => ({ lane: lane.index, zFrom: enclosure.zFrom, zTo: enclosure.zTo })) : []),
    ...(definition.hazards ?? []).flatMap((hazard) => hazardStretches(hazard, config, definition.lanes.length, rules)),
    ...[dozerStretch(definition, DOZER_CONFIG)].flatMap((stretch) => (stretch ? [stretch] : [])),
  ];
}

/** The double jump a bottle line shows at one speed. */
export interface IdealClimb {
  /** Take-off before the face (runner centre → face, m). */
  readonly takeOff: number;
  /** Second jump (s after take-off). */
  readonly doubleJumpAt: number;
  /** Feet above the take-off ground `t` seconds after take-off – the game's fixed-step flight. */
  readonly heightAt: (t: number) => number;
}

/**
 * The ideal double jump onto the top at `speed`: the face met at the moment with the widest second-jump
 * window (StepReach.bestArrival) – but never from further back than the run-up offers – and the second
 * jump in the middle of its window.
 */
export function idealClimb(climb: RoofClimb, rules: LayoutRules, speed: number): IdealClimb {
  const latest = (climb.runUp - rules.playerDepth / 2) / speed;
  const arrival = Math.max(climb.arrival.from, Math.min(climb.reach.bestArrival, latest, climb.arrival.to));
  const window = doubleJumpTriggerWindow(rules.physics, rules.maxStepHeight, climb.rise, arrival, rules.simulationStep);
  const doubleJumpAt = window ? (window.from + window.to) / 2 : rules.physics.jumpVelocity / rules.physics.gravity;
  return {
    takeOff: rules.playerDepth / 2 + arrival * speed,
    doubleJumpAt,
    heightAt: (t) => Math.max(0, jumpHeightAt(rules.physics, t, doubleJumpAt, rules.simulationStep)),
  };
}
